const MDX_FLAG_KEY = 'dex-tech-fe-mdx-enabled';

// Static context for a global server-side kill switch. If we later want
// per-user or per-merchant rollout, thread a richer context through here.
const SERVER_CONTEXT = { key: 'dex-tech-fe-server' };

interface MdxFlagPayload {
  enabled: boolean;
  // Optional list of slugs to force-fall-through to Contentful even when
  // `enabled` is true. Lets us yank a single broken page without disabling
  // the whole MDX rollout.
  blockedSlugs?: string[];
}

const DEFAULT_PAYLOAD: MdxFlagPayload = { enabled: false, blockedSlugs: [] };

function normalizePayload(value: unknown): MdxFlagPayload {
  if (typeof value === 'boolean') {
    // Tolerate the legacy boolean flag shape during the LD migration window.
    return { enabled: value, blockedSlugs: [] };
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return {
      enabled: Boolean(obj.enabled),
      blockedSlugs: Array.isArray(obj.blockedSlugs)
        ? (obj.blockedSlugs as unknown[]).filter(
            (entry): entry is string => typeof entry === 'string'
          )
        : [],
    };
  }
  return DEFAULT_PAYLOAD;
}

/**
 * Server-side LD load. Returns the resolved snapshot, or null on the
 * browser (LD wrapper is server-only — treeshaken by next.config.js's
 * SERVER_MODULES_TO_IGNORE_ON_CLIENT entry) or on any error. The caller
 * stuffs the snapshot into pageProps so the client can apply the
 * per-slug predicate during SPA navigation without re-asking LD.
 *
 * Flag shape (LaunchDarkly JSON variation):
 *   { "enabled": true, "blockedSlugs": ["build-basics/foo"] }
 *
 * Non-production: MDX_ENABLED_OVERRIDE=true short-circuits to enabled.
 */
async function loadMdxFlagPayload(): Promise<MdxFlagPayload | null> {
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.MDX_ENABLED_OVERRIDE === 'true'
  ) {
    return { enabled: true, blockedSlugs: [] };
  }

  if (typeof window !== 'undefined') {
    return null;
  }

  try {
    // eslint-disable-next-line @nx/enforce-module-boundaries
    const { initOrGetFeatureClient } = await import(
      '@squareup/dex-utils-shared-feature-flags-client-wrapper'
    );
    const client = await initOrGetFeatureClient();
    if (!client) {
      return null;
    }
    const raw = await client.getFlagValue(
      MDX_FLAG_KEY,
      SERVER_CONTEXT,
      DEFAULT_PAYLOAD
    );
    return normalizePayload(raw);
  } catch {
    return null;
  }
}

/**
 * Read the LD snapshot the server stuffed into __NEXT_DATA__ on the first
 * server-rendered page of this tab. Subsequent SPA navigations consult
 * this snapshot instead of re-asking LD; the value is frozen for the
 * session and only refreshes on a full reload. Returns null when there
 * is no snapshot (e.g. the first server-rendered page wasn't /docs/*).
 */
function readMdxFlagPayloadFromNextData(): MdxFlagPayload | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const nextData = (
    window as unknown as {
      __NEXT_DATA__?: {
        props?: { pageProps?: { data?: { mdxFlag?: unknown } } };
      };
    }
  ).__NEXT_DATA__;
  const raw = nextData?.props?.pageProps?.data?.mdxFlag;
  if (raw === null || raw === undefined) {
    return null;
  }
  return normalizePayload(raw);
}

/**
 * Apply the per-slug predicate to a snapshot. A missing snapshot (null /
 * undefined) defaults to false so we serve Contentful — the safe choice
 * when we can't consult LD.
 */
function isMdxEnabledForSlugFromPayload(
  slug: string,
  payload: MdxFlagPayload | null | undefined
): boolean {
  if (!payload || !payload.enabled) {
    return false;
  }
  return !(payload.blockedSlugs || []).includes(slug);
}

export {
  loadMdxFlagPayload,
  readMdxFlagPayloadFromNextData,
  isMdxEnabledForSlugFromPayload,
  MDX_FLAG_KEY,
};
export type { MdxFlagPayload };
